import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { CalendarDays, Clock, ImagePlus, Type, AlignLeft, Check, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface BarEvent {
  id: string
  title: string
  description: string
  date: string
  time: string
  photo: string
  createdAt: number
}

const STORAGE_KEY = 'bar-events'

export function getBarEvents(): BarEvent[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as BarEvent[]) : []
  } catch {
    return []
  }
}

export function saveBarEvent(event: Omit<BarEvent, 'id' | 'createdAt'>): BarEvent {
  const events = getBarEvents()
  const newEvent: BarEvent = {
    ...event,
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    createdAt: Date.now(),
  }
  events.unshift(newEvent)
  localStorage.setItem(STORAGE_KEY, JSON.stringify(events))
  return newEvent
}

export function BarEventForm() {
  const { t } = useTranslation('admin')

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [date, setDate] = useState('')
  const [time, setTime] = useState('')
  const [photo, setPhoto] = useState('')
  const [previewName, setPreviewName] = useState('')
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle')
  const [formError, setFormError] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const today = new Date().toISOString().split('T')[0]

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setFormError(true)
      return
    }

    const reader = new FileReader()
    reader.onloadend = () => {
      setPhoto(reader.result as string)
      setPreviewName(file.name)
      setFormError(false)
    }
    reader.readAsDataURL(file)
  }

  const handleRemovePhoto = () => {
    setPhoto('')
    setPreviewName('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const resetForm = () => {
    setTitle('')
    setDescription('')
    setDate('')
    setTime('')
    setPhoto('')
    setPreviewName('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!title.trim() || !description.trim() || !date || !time || !photo) {
      setFormError(true)
      return
    }

    setFormError(false)
    setStatus('submitting')

    // Simulate a brief submit state so the user gets feedback.
    setTimeout(() => {
      saveBarEvent({
        title: title.trim(),
        description: description.trim(),
        date,
        time,
        photo,
      })
      setStatus('success')
      resetForm()
      setTimeout(() => setStatus('idle'), 3000)
    }, 400)
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6 rounded-2xl bg-white p-6 shadow-sm sm:p-10"
    >
      {/* Photo upload */}
      <div className="block">
        <span className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
          <ImagePlus className="h-4 w-4 text-bar" aria-hidden="true" />
          {t('zone.event.photoLabel')}
        </span>
        <div
          className={cn(
            'relative flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-6 text-center transition-colors',
            photo
              ? 'border-bar bg-cream'
              : 'border-cord bg-cream hover:border-bar hover:bg-cream/80'
          )}
        >
          {photo ? (
            <div className="w-full space-y-4">
              <img
                src={photo}
                alt={t('zone.event.photoPreviewAlt')}
                className="mx-auto h-64 rounded-lg object-cover aspect-[3/4]"
              />
              <div className="flex items-center justify-center gap-3">
                <span className="text-sm font-medium text-ink">{previewName}</span>
                <button
                  type="button"
                  onClick={handleRemovePhoto}
                  className="rounded-full bg-pizza px-3 py-1 text-xs font-bold text-white transition-colors hover:bg-pizza-dark"
                >
                  {t('zone.event.removePhoto')}
                </button>
              </div>
            </div>
          ) : (
            <label className="cursor-pointer space-y-2">
              <ImagePlus className="mx-auto h-10 w-10 text-bar/60" aria-hidden="true" />
              <span className="block text-sm font-semibold text-ink">
                {t('zone.event.photoHint')}
              </span>
              <span className="block text-xs text-muted">
                {t('zone.event.photoFormats')}
              </span>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="sr-only"
              />
            </label>
          )}
        </div>
      </div>

      {/* Title */}
      <label className="block min-w-0">
        <span className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
          <Type className="h-4 w-4 text-bar" aria-hidden="true" />
          {t('zone.event.titleLabel')}
        </span>
        <input
          type="text"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value)
            setFormError(false)
          }}
          placeholder={t('zone.event.titlePlaceholder')}
          required
          className="block w-full rounded-xl border border-cord bg-cream px-4 py-3 text-ink transition-colors focus:border-bar focus:bg-white focus:outline-none focus:ring-2 focus:ring-bar/20"
        />
      </label>

      {/* Date and time */}
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-6 sm:grid-cols-2">
        <label className="block min-w-0">
          <span className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
            <CalendarDays className="h-4 w-4 text-bar" aria-hidden="true" />
            {t('zone.event.dateLabel')}
          </span>
          <div className="relative flex min-w-0">
            <input
              type="date"
              min={today}
              value={date}
              onChange={(e) => {
                setDate(e.target.value)
                setFormError(false)
              }}
              required
              className="w-full min-w-0 max-w-full rounded-xl border border-cord bg-cream px-4 py-3 text-ink transition-colors focus:border-bar focus:bg-white focus:outline-none focus:ring-2 focus:ring-bar/20"
            />
          </div>
        </label>

        <label className="block min-w-0">
          <span className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
            <Clock className="h-4 w-4 text-bar" aria-hidden="true" />
            {t('zone.event.timeLabel')}
          </span>
          <div className="relative flex min-w-0">
            <input
              type="time"
              value={time}
              onChange={(e) => {
                setTime(e.target.value)
                setFormError(false)
              }}
              required
              className="w-full min-w-0 max-w-full rounded-xl border border-cord bg-cream px-4 py-3 text-ink transition-colors focus:border-bar focus:bg-white focus:outline-none focus:ring-2 focus:ring-bar/20"
            />
          </div>
        </label>
      </div>

      {/* Description */}
      <label className="block min-w-0">
        <span className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
          <AlignLeft className="h-4 w-4 text-bar" aria-hidden="true" />
          {t('zone.event.descriptionLabel')}
        </span>
        <textarea
          value={description}
          onChange={(e) => {
            setDescription(e.target.value)
            setFormError(false)
          }}
          placeholder={t('zone.event.descriptionPlaceholder')}
          rows={5}
          required
          className="block w-full rounded-xl border border-cord bg-cream px-4 py-3 text-ink transition-colors focus:border-bar focus:bg-white focus:outline-none focus:ring-2 focus:ring-bar/20"
        />
      </label>

      {formError && (
        <p className="text-sm font-medium text-pizza">
          {t('zone.event.errorFill')}
        </p>
      )}

      {status === 'success' && (
        <div className="flex items-center gap-2 rounded-xl bg-maia/10 px-4 py-3 text-sm font-semibold text-maia-dark">
          <Check className="h-4 w-4" aria-hidden="true" />
          {t('zone.event.successMessage')}
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="w-full rounded-full bg-bar px-8 py-4 text-sm font-bold text-white shadow-lg transition-colors hover:bg-bar-dark disabled:cursor-not-allowed disabled:opacity-70"
      >
        {status === 'submitting' ? (
          <span className="inline-flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('zone.event.submitting')}
          </span>
        ) : (
          t('zone.event.submit')
        )}
      </button>
    </form>
  )
}
